import React, { useState, useEffect, useCallback } from 'react';
import { Fade, TableRow, TableCell, alpha, Typography, Chip } from '@mui/material';
import { TrendingUp as RoiIcon } from '@mui/icons-material';
import ReportLayout, { ReportColumn, SummaryCard, fmt2, fmtDate } from '../../components/reports/ReportLayout';
import ReportFilterBar from '../../components/reports/ReportFilterBar';
import { reportService, RoiRow } from '../../services/reportService';

const ROI_ACCENT = '#2E7D32';
const INVEST_CLR = '#2E7D32';
const RETURN_CLR = '#1565C0';

const typeColor = (t: string) => t === 'Invest' ? INVEST_CLR : RETURN_CLR;

const RoiInfoReport: React.FC = () => {
  const [rows,    setRows]    = useState<RoiRow[]>([]);
  const [totals,  setTotals]  = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<Record<string, string>>({
    date_from: '', date_to: '', investor_name: '', roi_type_name: '',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const p: any = {}; Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
      const res = await reportService.getRoiReport(p);
      setRows(res.results); setTotals((res.totals || {}) as any);
    } catch {} finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDownload = (fmt: 'excel' | 'pdf') => {
    const p: any = {}; Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return reportService.downloadRoi(fmt, p);
  };

  const columns: ReportColumn[] = [
    { key: 'invest_return_date',   label: 'Date',          format: v => fmtDate(v), noWrap: true },
    { key: 'investor_name',        label: 'Investor Name', width: 220 },
    {
      key: 'roi_type_name', label: 'ROI Type',
      format: v => (
        <Chip size="small" label={v}
          sx={{
            background: alpha(typeColor(v), 0.1),
            color:      typeColor(v),
            fontWeight: 700, height: 22, fontSize: '0.72rem',
          }}
        />
      ),
    },
    {
      key: 'invest_return_amount', label: 'Amount (Tk.)', align: 'right',
      format: (v, row) => (
        <strong style={{ color: typeColor((row as RoiRow).roi_type_name) }}>
          {fmt2(v)}
        </strong>
      ),
    },
    { key: 'remarks',    label: 'Remarks',    format: v => v || '—' },
    { key: 'created_by', label: 'Created By', format: v => <Typography variant="caption" color="text.secondary">{v}</Typography> },
  ];

  const summaryCards: SummaryCard[] = [
    { label: 'Records',           value: rows.length,                     color: ROI_ACCENT },
    { label: 'Total Invest (Tk.)',  value: fmt2(totals.total_invest ?? 0),  color: INVEST_CLR },
    { label: 'Total Return (Tk.)',  value: fmt2(totals.total_return ?? 0),  color: RETURN_CLR },
    { label: 'Total Amount (Tk.)', value: fmt2(totals.total_amount ?? 0),  color: '#FF9800'  },
  ];

  const totalsRow = rows.length > 0 && !loading ? (
    <TableRow sx={{ background: alpha(ROI_ACCENT, 0.06) }}>
      <TableCell colSpan={2} sx={{ py: 1.25, fontWeight: 800, fontSize: '0.82rem', color: 'text.secondary' }}>TOTALS</TableCell>
      <TableCell sx={{ py: 1.25 }}>
        <Chip size="small" label={`Invest: Tk. ${fmt2(totals.total_invest ?? 0)}`}
          sx={{ background: alpha(INVEST_CLR, 0.1), color: INVEST_CLR, fontWeight: 700, height: 20, fontSize: '0.7rem', mr: 0.5 }} />
        <Chip size="small" label={`Return: Tk. ${fmt2(totals.total_return ?? 0)}`}
          sx={{ background: alpha(RETURN_CLR, 0.1), color: RETURN_CLR, fontWeight: 700, height: 20, fontSize: '0.7rem' }} />
      </TableCell>
      <TableCell sx={{ py: 1.25, textAlign: 'right', fontWeight: 800, color: '#FF9800' }}>{fmt2(totals.total_amount ?? 0)}</TableCell>
      <TableCell colSpan={2} />
    </TableRow>
  ) : undefined;

  return (
    <Fade in timeout={400}><div>
      <ReportLayout title="ROI Information Report" subtitle="All investor contributions and returns by date"
        accentColor={ROI_ACCENT} columns={columns} rows={rows} loading={loading}
        tableMinWidth={1100}
        summaryCards={summaryCards} onRefresh={fetchData} onDownload={handleDownload}
        totalsRow={totalsRow} emptyIcon={<RoiIcon sx={{ fontSize: 40 }} />}
        filters={
          <ReportFilterBar accentColor={ROI_ACCENT} values={filters}
            onChange={(k, v) => setFilters(prev => ({ ...prev, [k]: v }))}
            onClear={() => setFilters({ date_from: '', date_to: '', investor_name: '', roi_type_name: '' })}
            fields={[
              { type: 'date',   key: 'date_from',     label: 'From' },
              { type: 'date',   key: 'date_to',        label: 'To' },
              { type: 'text',   key: 'investor_name',  label: 'Investor Name', minWidth: 180 },
              {
                type: 'select', key: 'roi_type_name', label: 'ROI Type', minWidth: 150,
                options: [
                  { value: 'Invest', label: 'Invest' },
                  { value: 'Return', label: 'Return' },
                ],
              },
            ]}
          />
        }
      />
    </div></Fade>
  );
};
export default RoiInfoReport;
